"use client";

import { useTranslations } from "next-intl";

import { cn } from "@/lib/utils";
import { StatusBadge } from "@/components/ui/status-badge";
import { BAND_TEXT, BAND_TEXT_ON_DARK } from "@/features/judge/lib/rating-band";
import { divisionBands, divisionLineMessage } from "../lib/division";
import type { ContestDivision } from "../types";

type Tone = "light" | "dark";

/** What the tag and its line read: a round's division names carry no preset or policy (`EX-DIV04`). */
type Band = Pick<ContestDivision, "label" | "ratedFrom" | "ratedBelow">;

/**
 * A contest's division, the one tag every screen that names a contest wears
 * (`EX-DIV03`, Wave 29 decision 6): "Div. 3" in Latin in both languages, in
 * `StatusBadge`'s `inverse` tone as admin draws it (`docs/03` §6), white on the
 * Judge's near-black. Its title carries the band in numbers. A custom band the
 * operator left unnamed says so in the reader's language.
 */
export function DivisionTag({
  division,
  locale,
  tone = "light",
}: {
  division: Band;
  locale: string;
  tone?: Tone;
}) {
  const t = useTranslations("contest.division");
  const line = divisionLineMessage(division, locale);
  return (
    <span data-testid="division-tag" title={t(line.key, line.values)} className="inline-flex shrink-0">
      <StatusBadge tone="inverse" onDark={tone === "dark"} label={division.label ?? t("customTag")} />
    </span>
  );
}

/**
 * Who the division rates, in numbers and in our band names (decision 6):
 * "Rated for ratings below 1600 · Newcomer to Solver", each band name in its
 * colour. `bands={false}` keeps the numbers alone where a line is short.
 */
export function DivisionLine({
  division,
  locale,
  tone = "light",
  bands = true,
  className,
}: {
  division: Band;
  locale: string;
  tone?: Tone;
  bands?: boolean;
  className?: string;
}) {
  const t = useTranslations("contest.division");
  const line = divisionLineMessage(division, locale);

  return (
    <span data-testid="division-line" className={className}>
      {t(line.key, line.values)}
      {bands ? (
        <>
          {" · "}
          <DivisionBands division={division} tone={tone} />
        </>
      ) : null}
    </span>
  );
}

/**
 * The band names alone, "Newcomer to Advanced" or "Master and up", each in its
 * colour: the second half of {@link DivisionLine}, and the round page's line
 * under a division's numbers (`EX-DIV06`).
 */
export function DivisionBands({ division, tone = "light" }: { division: Band; tone?: Tone }) {
  const t = useTranslations("contest.division");
  const tb = useTranslations("judge.ratingBands");
  const { low, high } = divisionBands(division);
  const colour = tone === "dark" ? BAND_TEXT_ON_DARK : BAND_TEXT;
  const name = (band: typeof low) => <b className={cn("font-semibold", colour[band])}>{tb(band)}</b>;
  return (
    <>
      {high === null
        ? t.rich("bandsAndUp", { low: () => name(low) })
        : high === low
          ? name(low)
          : t.rich("bandsRange", { low: () => name(low), high: () => name(high) })}
    </>
  );
}
